import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useHistory, useLocation } from 'react-router';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { remindersToKeepInTouchWithContacts } from '../../../../models/UiUtils';
import {
	getFeedItemViewModel,
	useContact,
	useContactFilterQuery,
	useContactSuggestionsInfiniteQuery,
	useDashboardFeed,
} from '../../../../queries';
import { ContactViewModel } from '../../../../viewmodels/AppViewModels';
import { CloseButton } from '../../../components/CloseButton';
import { EditableContact } from '../../../components/contacts/EditableContact';
import { FabContext } from '../../../components/FabContext';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { DisclosureIcon } from '../../../components/svgs/icons/DisclosureIcon';
import { ROUTES } from '../../../constants/routes';
import { brandPrimary } from '../../../styles/colors';
import { baseStyleSheet } from '../../../styles/styles';
import { ContactActivity } from '../../People/Contact/Activity';
import { useFocusedViewContext } from './FocusedViewProvider/context';
import { styleSheet } from './styles';

export const KIT_FEED_TYPE = 'Reminder';

export function FocusedContact() {
	const location = useLocation();
	const history = useHistory();
	const searchParams = Object.fromEntries(new URLSearchParams(location.search));
	const fromPath = searchParams.fromPath ?? ROUTES.contacts.people;
	const mode = searchParams.mode as 'contact' | 'kit' | 'suggestions';
	const initialContactIds = searchParams.initialContactIds?.split(',') ?? [];
	const initialContactIdsToOmit = searchParams.initialContactIdsToOmit?.split(',') ?? [];
	const initialFilterCriteriaList = searchParams.initialFilterCriteriaList
		? (JSON.parse(searchParams.initialFilterCriteriaList) as Api.IContactFilterCriteria[])
		: [];
	const ownershipFilter = searchParams.ownershipFilter
		? (JSON.parse(searchParams.ownershipFilter) as Api.IOwnershipFilter)
		: undefined;
	const [totalCount, setTotalCount] = React.useState<number>(initialContactIds?.length ?? 0);
	const { setContactIds, contactIds, selectedContactId, setSelectedContactId } = useFocusedViewContext();

	React.useEffect(() => {
		if (initialContactIds?.length > 0) {
			setContactIds(initialContactIds);
			setSelectedContactId(initialContactIds[0]);
		}
		return () => {
			setContactIds([]);
			setSelectedContactId(null);
			setTotalCount(0);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const contactQuery = useContact({
		contactId: selectedContactId,
		enabled: !!selectedContactId,
	});

	const isLoading = contactQuery.isPending;

	const contact = contactQuery.data || null;

	const userSession = useUserSession();

	// If selected contact ids were not passed in then enable a paged filtering mode for contacts
	const contactFilterQuery = useContactFilterQuery({
		enabled: mode === 'contact' && initialContactIds?.length === 0,
		filterRequest: {
			filter: {
				criteria: initialFilterCriteriaList,
			},
			ownershipFilter,
		},
	});

	// If the kit button directs here it does not support paged fetching so fetch 100
	const dashboardFeedQuery = useDashboardFeed({
		enabled: mode === 'kit',
		pageSize: 100,
		typeOf: KIT_FEED_TYPE,
	});

	const contactSuggestionsQuery = useContactSuggestionsInfiniteQuery({
		enabled: mode === 'suggestions',
		pageSize: 25,
	});

	React.useEffect(() => {
		if (mode !== 'contact') {
			return;
		}
		const loadedContactIds = contactFilterQuery.data?.pages
			?.map(page => {
				return page.values;
			})
			.flat()
			.map(item => item.id)
			.filter(item => initialContactIdsToOmit.indexOf(item) === -1);

		if (loadedContactIds?.length > 0) {
			setContactIds(loadedContactIds);
			if (!selectedContactId) {
				setSelectedContactId(loadedContactIds[0]);
			}
			setTotalCount(contactFilterQuery.data?.pages?.[0]?.totalCount ?? 0);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contactFilterQuery.data]);

	React.useEffect(() => {
		if (mode !== 'kit') {
			return;
		}
		const values = dashboardFeedQuery.data?.values ?? [];
		const actionItemVms = values
			.map((item: Api.IDashboardFeedItem) => getFeedItemViewModel({ feedItem: item, userSession }))
			.filter((itemVm: any) => itemVm instanceof Api.ActionItemViewModel) as Api.ActionItemViewModel[];
		const { contacts } = remindersToKeepInTouchWithContacts(actionItemVms);
		const loadedContactIds = contacts
			.map(loadedContact => loadedContact.id)
			.filter(loadedContact => initialContactIdsToOmit.indexOf(loadedContact) === -1);

		if (loadedContactIds?.length > 0) {
			setContactIds(loadedContactIds);
			if (!selectedContactId) {
				setSelectedContactId(loadedContactIds[0]);
			}
			setTotalCount(dashboardFeedQuery.data?.totalCount ?? 0);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dashboardFeedQuery.data]);

	React.useEffect(() => {
		if (mode !== 'suggestions') {
			return;
		}
		const loadedContactIds = contactSuggestionsQuery.data?.pages
			?.map(page => {
				return page.values;
			})
			.flat()
			.map(item => item.id)
			.filter(item => initialContactIdsToOmit.indexOf(item) === -1);

		if (loadedContactIds?.length > 0) {
			setContactIds(loadedContactIds);
			if (!selectedContactId) {
				setSelectedContactId(loadedContactIds[0]);
			}
			setTotalCount(contactSuggestionsQuery.data?.pages?.[0]?.totalCount ?? 0);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contactSuggestionsQuery.data]);

	const next = async () => {
		const currentIndex = contactIds.indexOf(selectedContactId);

		const isLastItem = currentIndex + 1 === contactIds.length;
		if (isLastItem) {
			if (totalCount === contactIds.length) {
				return;
			}
			if (mode === 'contact') {
				await contactFilterQuery.fetchNextPage();
			} else if (mode === 'kit') {
				await contactFilterQuery.fetchNextPage();
			}
		}

		const nextContactId = contactIds[currentIndex + 1];
		setSelectedContactId(nextContactId);
	};

	const back = () => {
		const currentIndex = contactIds.indexOf(selectedContactId);
		if (currentIndex === 0) {
			return;
		}
		const nextContactId = contactIds[currentIndex - 1];
		setSelectedContactId(nextContactId);
	};

	if (!contactIds?.length) {
		return null;
	}
	const contactVm = contact ? new ContactViewModel(userSession, contact) : null;

	const currentContactPosition = contactIds.indexOf(selectedContactId) + 1;
	const countHeader = `${currentContactPosition}/${totalCount}`;

	return (
		<>
			<div className={css(styleSheet.navHeader)}>
				<div className={css(styleSheet.navHeaderLeft)}>
					<button onClick={back}>
						<div className={css(styleSheet.navIcon, styleSheet.backIcon)}>
							<DisclosureIcon type='chevron' fillColor={brandPrimary} />
						</div>
					</button>
				</div>
				<div className={css(styleSheet.navHeaderCenter)}>{countHeader}</div>
				<div className={css(styleSheet.navHeaderLeft)}>
					<button onClick={next}>
						<div className={css(styleSheet.navIcon, styleSheet.nextIcon)}>
							<DisclosureIcon type='chevron' fillColor={brandPrimary} />
						</div>
					</button>
					<CloseButton fillColor='white' onClick={() => history.push(fromPath)} />
				</div>
			</div>
			<div className={`${css(styleSheet.container)}`}>
				{!contactVm ? <LoadingSpinner className={css(baseStyleSheet.absoluteCenter)} /> : null}
				{contactVm ? (
					<>
						<div className={css(styleSheet.splitMaster)}>
							<EditableContact contact={contactVm} hideEditButton={false} isLoading={isLoading} />
						</div>
						<div className={css(styleSheet.splitDetail)}>
							<ContactActivity className={[styleSheet.activity]} contact={contactVm} userSession={userSession} />
						</div>
					</>
				) : null}
				<FabContext appearance={{ hidden: true }} />
			</div>
		</>
	);
}
